import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import {
  TrendingUp, TrendingDown, Users, Globe, Share2, Star, Plus,
  Mail, Megaphone, RefreshCw, Sparkles, BarChart2,
  Target, Headphones, Heart, Award, UserPlus, Ticket,
  BookOpen, ClipboardList, ShoppingBag, Briefcase, ArrowRight,
  Video, FileText, CheckCircle2, AlertTriangle, Building2, Crown,
  Activity, Zap, PieChart, Shield
} from 'lucide-react';
import { useCurrency } from '../contexts/CurrencyContext';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend,
  BarChart, Bar, PieChart as RPieChart, Pie, Cell } from 'recharts';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator } from '../components/ui/dropdown-menu';
import { cn } from '../lib/utils';
import { PageInfoTooltip } from '../components/shared/PageInfoTooltip';
import { useAuth } from '../contexts/AuthContext';
import { useTour } from '../contexts/TourContext';
import { KaharianHealthBadge } from './KaharianHealthPage';
import NewSubscriberDashboard from './SubscriberDashboard';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const TrendBadge = ({ value }) => {
  if (value === undefined || value === null) return null;
  const pos = value >= 0;
  return (
    <span className={cn("inline-flex items-center gap-0.5 text-xs font-medium", pos ? "text-emerald-600" : "text-red-500")}>
      {pos ? <TrendingUp className="h-3 w-3" /> : <TrendingDown className="h-3 w-3" />}
      {Math.abs(value)}%
    </span>
  );
};

const StatCard = ({ label, value, icon: Icon, trend, color, onClick }) => (
  <Card className={cn("bg-white border-gray-border hover:shadow-sm transition-shadow", onClick && "cursor-pointer")}
    onClick={onClick} data-testid={`stat-${label.toLowerCase().replace(/\s+/g, '-')}`}>
    <CardContent className="p-3 sm:p-4">
      <div className="flex items-start justify-between mb-2">
        <div className={cn("w-9 h-9 rounded-xl flex items-center justify-center", color || "bg-emerald-50")}>
          <Icon className="h-4 w-4 text-inherit" style={{ color: 'inherit' }} />
        </div>
        <TrendBadge value={trend} />
      </div>
      <p className="text-lg sm:text-xl font-heading font-bold text-text-primary">{value}</p>
      <p className="text-[11px] text-gray-medium mt-0.5">{label}</p>
    </CardContent>
  </Card>
);

const ActivityItem = ({ action, time, status }) => {
  const statusColors = { success: 'bg-emerald-500', warning: 'bg-amber-500', info: 'bg-blue-500' };
  return (
    <div className="flex items-start gap-3 py-2.5 border-b border-gray-100 last:border-0">
      <div className={cn("w-1.5 h-1.5 rounded-full mt-2 flex-shrink-0", statusColors[status] || 'bg-gray-400')} />
      <div className="flex-1 min-w-0">
        <p className="text-sm text-text-primary">{action}</p>
        <p className="text-[10px] text-gray-medium">{time}</p>
      </div>
    </div>
  );
};

const CHART_COLORS = ['#10B981', '#3B82F6', '#F59E0B', '#EF4444', '#8B5CF6', '#EC4899'];

// ═══════════════════════════════════════════════════════════════
// SUPER ADMIN DASHBOARD
// ═══════════════════════════════════════════════════════════════
function SuperAdminDashboard({ stats, activities, navigate }) {
  return (
    <div className="space-y-5" data-testid="super-admin-dashboard">
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
        <StatCard label="Country Operators" value={stats?.total_operators || 0} icon={Globe} trend={stats?.trends?.operators} color="bg-blue-50" />
        <StatCard label="Subscribers" value={stats?.total_subscribers || 0} icon={Building2} trend={stats?.trends?.subscribers} color="bg-emerald-50" />
        <StatCard label="Total Staff" value={stats?.total_staff || 0} icon={Users} color="bg-purple-50" />
        <StatCard label="New This Week" value={stats?.new_users_week || 0} icon={UserPlus} trend={stats?.trends?.users} color="bg-amber-50" />
        <StatCard label="Total Platform Users" value={stats?.total_users || 0} icon={Crown} color="bg-rose-50" />
      </div>

      <div className="cursor-pointer" onClick={() => navigate('/settings/api-health')} data-testid="dashboard-health-badge">
        <KaharianHealthBadge />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        {/* Operator Performance Table */}
        <Card className="lg:col-span-2 bg-white border-gray-border">
          <CardHeader className="pb-2">
            <CardTitle className="text-base font-heading flex items-center gap-2">
              <Globe className="h-4 w-4 text-blue-500" />Country/Area Operator Performance
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead>
                  <tr className="border-b border-gray-200">
                    <th className="text-left py-2 text-gray-500 font-medium">Operator</th>
                    <th className="text-left py-2 text-gray-500 font-medium">Email</th>
                    <th className="text-right py-2 text-gray-500 font-medium">Clients</th>
                    <th className="text-right py-2 text-gray-500 font-medium">Leads</th>
                  </tr>
                </thead>
                <tbody>
                  {(stats?.operator_stats || []).map((op, i) => (
                    <tr key={i} className="border-b border-gray-50 hover:bg-gray-50" data-testid={`operator-row-${i}`}>
                      <td className="py-2 font-medium text-gray-800">{op.name}</td>
                      <td className="py-2 text-gray-500">{op.email}</td>
                      <td className="py-2 text-right font-semibold text-blue-600">{op.clients}</td>
                      <td className="py-2 text-right font-semibold text-emerald-600">{op.leads}</td>
                    </tr>
                  ))}
                  {(!stats?.operator_stats || stats.operator_stats.length === 0) && (
                    <tr><td colSpan={4} className="py-6 text-center text-gray-400">No operators yet</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>

        {/* Platform-wide stats */}
        <Card className="bg-white border-gray-border">
          <CardHeader className="pb-2">
            <CardTitle className="text-base font-heading">Platform Totals</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="flex items-center justify-between p-2.5 bg-blue-50 rounded-lg">
              <div className="flex items-center gap-2"><Target className="h-4 w-4 text-blue-500" /><span className="text-xs text-gray-700">Total Leads</span></div>
              <span className="text-sm font-bold text-blue-700">{(stats?.total_leads || 0).toLocaleString()}</span>
            </div>
            <div className="flex items-center justify-between p-2.5 bg-teal-50 rounded-lg">
              <div className="flex items-center gap-2"><Ticket className="h-4 w-4 text-teal-500" /><span className="text-xs text-gray-700">Total Tickets</span></div>
              <span className="text-sm font-bold text-teal-700">{(stats?.total_tickets || 0).toLocaleString()}</span>
            </div>
            <div className="flex items-center justify-between p-2.5 bg-purple-50 rounded-lg">
              <div className="flex items-center gap-2"><Video className="h-4 w-4 text-purple-500" /><span className="text-xs text-gray-700">Total Meetings</span></div>
              <span className="text-sm font-bold text-purple-700">{(stats?.total_meetings || 0).toLocaleString()}</span>
            </div>
            <div className="flex items-center justify-between p-2.5 bg-amber-50 rounded-lg">
              <div className="flex items-center gap-2"><Sparkles className="h-4 w-4 text-amber-500" /><span className="text-xs text-gray-700">AI Content Generated</span></div>
              <span className="text-sm font-bold text-amber-700">{(stats?.total_content || 0).toLocaleString()}</span>
            </div>
          </CardContent>
        </Card>
      </div>

      <ActivityFeed activities={activities} />
    </div>
  );
}


// ═══════════════════════════════════════════════════════════════
// COUNTRY OPERATOR DASHBOARD
// ═══════════════════════════════════════════════════════════════
function CountryOperatorDashboard({ stats, activities, navigate }) {
  const featureData = (stats?.feature_usage || []).map((f, i) => ({ ...f, fill: CHART_COLORS[i % CHART_COLORS.length] }));
  const planBreakdown = stats?.plan_breakdown || {};

  return (
    <div className="space-y-5" data-testid="operator-dashboard">
      {/* Territory Badge */}
      {stats?.country && (
        <div className="flex items-center gap-2 text-xs text-gray-500" data-testid="operator-territory">
          <Globe className="h-3.5 w-3.5 text-blue-500" />
          <span>Territory: <strong className="text-gray-800">{stats.country}</strong></span>
        </div>
      )}

      {/* Key Metrics */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
        <StatCard label="Total Clients" value={stats?.total_clients || 0} icon={Building2} trend={stats?.trends?.clients} color="bg-blue-50" />
        <StatCard label="New This Month" value={stats?.new_clients_month || 0} icon={UserPlus} color="bg-emerald-50" />
        <StatCard label="Total Leads" value={stats?.total_leads || 0} icon={Target} trend={stats?.trends?.leads} color="bg-amber-50" />
        <StatCard label="Total Staff" value={stats?.total_staff || 0} icon={Users} color="bg-indigo-50" />
        <StatCard label="AI Content" value={stats?.total_content || 0} icon={Sparkles} trend={stats?.trends?.content} color="bg-purple-50" />
      </div>

      {/* Secondary Metrics */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <MiniCard label="Support Tickets" value={stats?.total_tickets || 0} icon={Ticket} color="bg-teal-50" />
        <MiniCard label="Meetings" value={stats?.total_meetings || 0} icon={Video} color="bg-cyan-50" />
        {Object.entries(planBreakdown).map(([plan, count]) => (
          <MiniCard key={plan} label={`${plan.charAt(0).toUpperCase() + plan.slice(1)} Plan`} value={count} icon={Crown} color="bg-rose-50" />
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        {/* Top Clients Table */}
        <Card className="lg:col-span-2 bg-white border-gray-border">
          <CardHeader className="pb-2">
            <CardTitle className="text-base font-heading flex items-center gap-2">
              <Award className="h-4 w-4 text-amber-500" />Top Clients by Activity
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead>
                  <tr className="border-b border-gray-200">
                    <th className="text-left py-2 text-gray-500 font-medium">Client</th>
                    <th className="text-left py-2 text-gray-500 font-medium">Plan</th>
                    <th className="text-right py-2 text-gray-500 font-medium">Leads</th>
                    <th className="text-right py-2 text-gray-500 font-medium">Tickets</th>
                    <th className="text-right py-2 text-gray-500 font-medium">AI Content</th>
                    <th className="text-right py-2 text-gray-500 font-medium">Staff</th>
                  </tr>
                </thead>
                <tbody>
                  {(stats?.top_clients || []).map((c, i) => (
                    <tr key={i} className="border-b border-gray-50 hover:bg-gray-50" data-testid={`client-row-${i}`}>
                      <td className="py-2">
                        <p className="font-medium text-gray-800">{c.name}</p>
                        <p className="text-[10px] text-gray-400">{c.business_name || c.email}</p>
                      </td>
                      <td className="py-2"><Badge className="text-[9px] bg-emerald-50 text-emerald-600 capitalize">{c.plan}</Badge></td>
                      <td className="py-2 text-right font-semibold">{c.leads}</td>
                      <td className="py-2 text-right font-semibold">{c.tickets}</td>
                      <td className="py-2 text-right font-semibold">{c.content_generated}</td>
                      <td className="py-2 text-right font-semibold text-indigo-600">{c.staff_count}</td>
                    </tr>
                  ))}
                  {(!stats?.top_clients || stats.top_clients.length === 0) && (
                    <tr><td colSpan={6} className="py-6 text-center text-gray-400">No clients in your territory yet</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>

        {/* Feature Usage */}
        <Card className="bg-white border-gray-border">
          <CardHeader className="pb-2">
            <CardTitle className="text-base font-heading flex items-center gap-2">
              <Zap className="h-4 w-4 text-amber-500" />Most Used Features
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2.5">
            {featureData.map((f, i) => (
              <div key={i} className="flex items-center justify-between p-2.5 rounded-lg bg-gray-50" data-testid={`feature-${i}`}>
                <span className="text-xs font-medium text-gray-700">{f.feature}</span>
                <Badge className="text-xs font-bold" style={{ backgroundColor: f.fill + '20', color: f.fill }}>{f.usage}</Badge>
              </div>
            ))}
            {featureData.length === 0 && (
              <p className="text-center text-xs text-gray-400 py-4">No feature usage data yet</p>
            )}
          </CardContent>
        </Card>
      </div>

      <ActivityFeed activities={activities} />
    </div>
  );
}


// ═══════════════════════════════════════════════════════════════
// SUBSCRIBER ADMIN (client_admin) DASHBOARD
// ═══════════════════════════════════════════════════════════════
function SubscriberDashboard({ stats, activities, navigate, format: fc }) {
  const stageData = Object.entries(stats?.stage_breakdown || {}).map(([stage, count], i) => ({
    name: stage.replace(/_/g, ' '),
    value: count,
    fill: CHART_COLORS[i % CHART_COLORS.length],
  }));

  return (
    <div className="space-y-5" data-testid="subscriber-dashboard">
      {/* Key Metrics */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <StatCard label="Leads This Month" value={stats?.leads_month || 0} icon={Target} trend={stats?.trends?.leads} color="bg-blue-50"
          onClick={() => navigate('/leads/pipeline')} />
        <StatCard label="Open Tickets" value={stats?.open_tickets || 0} icon={Headphones} trend={stats?.trends?.tickets} color="bg-teal-50"
          onClick={() => navigate('/service/helpdesk')} />
        <StatCard label="AI Content Generated" value={stats?.content_generated || 0} icon={Sparkles} trend={stats?.trends?.content} color="bg-purple-50"
          onClick={() => navigate('/content')} />
        <StatCard label="Reputation Score" value={`${stats?.reputation_score || 0}/5`} icon={Star} trend={stats?.trends?.reputation} color="bg-amber-50" />
      </div>

      {/* Business Performance Row */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
        <MiniCard label="Pipeline Value" value={fc(stats?.pipeline_value || 0)} icon={TrendingUp} color="bg-green-50" />
        <MiniCard label="Total Leads" value={stats?.leads_total || 0} icon={Target} color="bg-blue-50" />
        <MiniCard label="Staff Members" value={stats?.staff_count || 0} icon={Users} color="bg-indigo-50" />
        <MiniCard label="Products/Services" value={stats?.products_count || 0} icon={ShoppingBag} color="bg-purple-50" />
        <MiniCard label="Loyalty Members" value={stats?.loyalty_members || 0} icon={Award} color="bg-amber-50" />
        <MiniCard label="Meetings Held" value={stats?.meetings_count || 0} icon={Video} color="bg-cyan-50" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        {/* Lead Pipeline Breakdown */}
        <Card className="bg-white border-gray-border">
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <CardTitle className="text-sm font-heading flex items-center gap-2"><PieChart className="h-4 w-4 text-blue-500" />Pipeline Stages</CardTitle>
              <Button variant="ghost" size="sm" className="text-[10px] h-6 gap-1" onClick={() => navigate('/leads/pipeline')}>View <ArrowRight className="h-3 w-3" /></Button>
            </div>
          </CardHeader>
          <CardContent>
            {stageData.length > 0 ? (
              <div className="h-[180px]">
                <ResponsiveContainer width="100%" height="100%">
                  <RPieChart>
                    <Pie data={stageData} cx="50%" cy="50%" outerRadius={70} dataKey="value" nameKey="name" label={({ name, value }) => `${name}: ${value}`} labelLine={false}>
                      {stageData.map((_, i) => <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />)}
                    </Pie>
                    <Tooltip contentStyle={{ fontSize: '11px', borderRadius: '8px' }} />
                  </RPieChart>
                </ResponsiveContainer>
              </div>
            ) : (
              <p className="text-center text-xs text-gray-400 py-8">No pipeline data yet</p>
            )}
          </CardContent>
        </Card>

        {/* Marketing & Sales Metrics */}
        <Card className="bg-white border-gray-border">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-heading flex items-center gap-2"><BarChart2 className="h-4 w-4 text-emerald-500" />Marketing & Sales</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2.5">
            <MetricRow label="Social Posts" value={stats?.social_posts || 0} icon={Share2} max={Math.max(stats?.social_posts || 1, stats?.email_campaigns || 1, stats?.content_month || 1) * 1.2} color="#3B82F6" />
            <MetricRow label="Email Campaigns" value={stats?.email_campaigns || 0} icon={Mail} max={Math.max(stats?.social_posts || 1, stats?.email_campaigns || 1, stats?.content_month || 1) * 1.2} color="#8B5CF6" />
            <MetricRow label="AI Content (Month)" value={stats?.content_month || 0} icon={Sparkles} max={Math.max(stats?.social_posts || 1, stats?.email_campaigns || 1, stats?.content_month || 1) * 1.2} color="#10B981" />
            <MetricRow label="Leads This Week" value={stats?.leads_week || 0} icon={Target} max={Math.max(stats?.leads_month || 1, 1)} color="#F59E0B" />
            <MetricRow label="Reviews" value={stats?.review_count || 0} icon={Star} max={100} color="#EC4899" />
          </CardContent>
        </Card>

        {/* Service & Operations */}
        <Card className="bg-white border-gray-border">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-heading flex items-center gap-2"><Shield className="h-4 w-4 text-teal-500" />Service & Goals</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2.5">
            <div className="flex items-center justify-between p-2 bg-teal-50 rounded-lg">
              <span className="text-xs text-gray-700">Tickets Resolved</span>
              <span className="text-sm font-bold text-teal-700">{stats?.resolved_tickets || 0}</span>
            </div>
            <div className="flex items-center justify-between p-2 bg-orange-50 rounded-lg">
              <span className="text-xs text-gray-700">Tickets Open</span>
              <span className="text-sm font-bold text-orange-700">{stats?.open_tickets || 0}</span>
            </div>
            <div className="flex items-center justify-between p-2 bg-emerald-50 rounded-lg">
              <span className="text-xs text-gray-700">Goals Completed</span>
              <span className="text-sm font-bold text-emerald-700">{stats?.completed_goals || 0}</span>
            </div>
            <div className="flex items-center justify-between p-2 bg-blue-50 rounded-lg">
              <span className="text-xs text-gray-700">Goals Active</span>
              <span className="text-sm font-bold text-blue-700">{stats?.active_goals || 0}</span>
            </div>
            <div className="flex items-center justify-between p-2 bg-indigo-50 rounded-lg">
              <span className="text-xs text-gray-700">Employees</span>
              <span className="text-sm font-bold text-indigo-700">{stats?.employees_count || 0}</span>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Feature Usage */}
      <Card className="bg-white border-gray-border" data-testid="feature-usage-chart">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-heading flex items-center gap-2"><Activity className="h-4 w-4 text-emerald-500" />Top Features Used</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="h-[160px]">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={(stats?.feature_usage || []).slice(0, 6)} layout="vertical" margin={{ left: 100, right: 20 }}>
                <XAxis type="number" tick={{ fontSize: 10 }} />
                <YAxis type="category" dataKey="feature" tick={{ fontSize: 10 }} width={100} />
                <Tooltip contentStyle={{ fontSize: '11px', borderRadius: '8px' }} />
                <Bar dataKey="count" fill="#10B981" radius={[0, 4, 4, 0]} barSize={16}>
                  {(stats?.feature_usage || []).map((_, i) => <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </CardContent>
      </Card>

      {/* Quick Links */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3" data-testid="quick-links">
        {[
          { label: 'CRM Pipeline', icon: Target, path: '/leads/pipeline', color: 'bg-blue-50 text-blue-600' },
          { label: 'Help Desk', icon: Ticket, path: '/service/helpdesk', color: 'bg-teal-50 text-teal-600' },
          { label: 'AI Content', icon: Sparkles, path: '/content', color: 'bg-purple-50 text-purple-600' },
          { label: 'Performance', icon: BarChart2, path: '/operations/evaluations', color: 'bg-amber-50 text-amber-600' },
          { label: 'KonnectMeet', icon: Video, path: '/meeting', color: 'bg-cyan-50 text-cyan-600' },
          { label: 'Analytics', icon: PieChart, path: '/analytics', color: 'bg-rose-50 text-rose-600' },
        ].map(q => (
          <Card key={q.label} className="border-gray-200 hover:shadow-sm transition-shadow cursor-pointer" onClick={() => navigate(q.path)}>
            <CardContent className="p-3 flex items-center gap-2.5">
              <div className={`w-8 h-8 rounded-lg ${q.color} flex items-center justify-center flex-shrink-0`}><q.icon className="h-4 w-4" /></div>
              <span className="text-xs font-medium text-gray-700">{q.label}</span>
            </CardContent>
          </Card>
        ))}
      </div>

      <ActivityFeed activities={activities} />
    </div>
  );
}


// ═══════════════════════════════════════════════════════════════
// STAFF DASHBOARD
// ═══════════════════════════════════════════════════════════════
function StaffDashboard({ stats, navigate }) {
  return (
    <div className="space-y-5" data-testid="staff-dashboard">
      {/* My Performance */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <StatCard label="My Leads" value={stats?.my_leads || 0} icon={Target} trend={stats?.trends?.leads} color="bg-blue-50" />
        <StatCard label="Leads This Month" value={stats?.my_leads_month || 0} icon={TrendingUp} color="bg-emerald-50" />
        <StatCard label="Tickets Resolved" value={stats?.my_tickets_resolved || 0} icon={CheckCircle2} trend={stats?.trends?.tickets} color="bg-teal-50" />
        <StatCard label="Open Tickets" value={stats?.my_tickets_open || 0} icon={Headphones} color="bg-orange-50" />
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <StatCard label="AI Content Created" value={stats?.my_content || 0} icon={Sparkles} color="bg-purple-50" />
        <StatCard label="Meetings Attended" value={stats?.my_meetings || 0} icon={Video} color="bg-cyan-50" />
        <StatCard label="Latest KPI Score"
          value={stats?.latest_score !== null && stats?.latest_score !== undefined ? `${stats.latest_score}/100` : 'N/A'}
          icon={BarChart2} color="bg-amber-50" />
        <StatCard label="Latest Rating"
          value={stats?.latest_rating ? stats.latest_rating.replace(/_/g, ' ') : 'N/A'}
          icon={Award} color={stats?.latest_rating === 'exceptional' ? 'bg-emerald-50' : 'bg-gray-50'} />
      </div>

      {/* My Goals */}
      {(stats?.my_goals || []).length > 0 && (
        <Card className="bg-white border-gray-border" data-testid="staff-goals">
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <CardTitle className="text-sm font-heading flex items-center gap-2"><Target className="h-4 w-4 text-blue-500" />My Active Goals</CardTitle>
              <Button variant="ghost" size="sm" className="text-[10px] h-6 gap-1" onClick={() => navigate('/operations/evaluations')}>All Goals <ArrowRight className="h-3 w-3" /></Button>
            </div>
          </CardHeader>
          <CardContent className="space-y-2">
            {stats.my_goals.map((g, i) => (
              <div key={i} className="flex items-center gap-3 p-2.5 bg-gray-50 rounded-lg" data-testid={`my-goal-${i}`}>
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-medium truncate">{g.title}</p>
                  <p className="text-[10px] text-gray-400">{g.current_value}/{g.target_value} {g.unit}</p>
                </div>
                <div className="w-20 shrink-0">
                  <Progress value={g.progress || 0} className="h-1.5" />
                  <p className="text-[9px] text-gray-400 text-right mt-0.5">{g.progress || 0}%</p>
                </div>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      {/* Evaluation History */}
      {(stats?.evaluations || []).length > 0 && (
        <Card className="bg-white border-gray-border" data-testid="staff-evals">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-heading flex items-center gap-2"><ClipboardList className="h-4 w-4 text-purple-500" />Recent Evaluations</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {stats.evaluations.map((e, i) => (
                <div key={i} className="flex items-center justify-between p-2.5 bg-gray-50 rounded-lg" data-testid={`eval-${i}`}>
                  <div>
                    <p className="text-xs font-medium">{e.period}</p>
                    <p className="text-[10px] text-gray-400 capitalize">{(e.rating || '').replace(/_/g, ' ')}</p>
                  </div>
                  <Badge className={cn("text-xs",
                    (e.overall_score || 0) >= 80 ? "bg-emerald-50 text-emerald-600" :
                    (e.overall_score || 0) >= 60 ? "bg-amber-50 text-amber-600" : "bg-red-50 text-red-600"
                  )}>{e.overall_score || 0}/100</Badge>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* No data state */}
      {(!stats?.my_goals || stats.my_goals.length === 0) && (!stats?.evaluations || stats.evaluations.length === 0) && (
        <Card className="bg-white border-gray-border">
          <CardContent className="p-8 text-center">
            <Activity className="h-8 w-8 text-gray-300 mx-auto mb-2" />
            <p className="text-sm text-gray-500">Start creating leads, resolving tickets, and generating content to see your contribution metrics here.</p>
          </CardContent>
        </Card>
      )}

      {/* Quick Links */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {[
          { label: 'My Leads', icon: Target, path: '/leads/pipeline', color: 'bg-blue-50 text-blue-600' },
          { label: 'Help Desk', icon: Ticket, path: '/service/helpdesk', color: 'bg-teal-50 text-teal-600' },
          { label: 'AI Content', icon: Sparkles, path: '/content', color: 'bg-purple-50 text-purple-600' },
          { label: 'KonnectMeet', icon: Video, path: '/meeting', color: 'bg-cyan-50 text-cyan-600' },
        ].map(q => (
          <Card key={q.label} className="border-gray-200 hover:shadow-sm transition-shadow cursor-pointer" onClick={() => navigate(q.path)}>
            <CardContent className="p-3 flex items-center gap-2.5">
              <div className={`w-8 h-8 rounded-lg ${q.color} flex items-center justify-center flex-shrink-0`}><q.icon className="h-4 w-4" /></div>
              <span className="text-xs font-medium text-gray-700">{q.label}</span>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
}


// ═══════════════════════════════════════════════════════════════
// SHARED COMPONENTS
// ═══════════════════════════════════════════════════════════════
function MiniCard({ label, value, icon: Icon, color }) {
  return (
    <div className={cn("p-3 rounded-xl", color)} data-testid={`mini-${label.toLowerCase().replace(/\s+/g, '-')}`}>
      <div className="flex items-center gap-2 mb-1">
        <Icon className="h-3.5 w-3.5 text-gray-600" />
        <span className="text-[10px] text-gray-500">{label}</span>
      </div>
      <p className="text-sm font-bold text-gray-800">{typeof value === 'number' ? value.toLocaleString() : value}</p>
    </div>
  );
}

function MetricRow({ label, value, icon: Icon, max, color }) {
  const pct = max > 0 ? Math.min(100, (value / max) * 100) : 0;
  return (
    <div className="flex items-center gap-2.5">
      <Icon className="h-3.5 w-3.5 text-gray-400 shrink-0" />
      <div className="flex-1">
        <div className="flex items-center justify-between mb-0.5">
          <span className="text-[10px] text-gray-600">{label}</span>
          <span className="text-[10px] font-bold text-gray-800">{value}</span>
        </div>
        <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
          <div className="h-full rounded-full transition-all" style={{ width: `${pct}%`, backgroundColor: color }} />
        </div>
      </div>
    </div>
  );
}

function ActivityFeed({ activities }) {
  return (
    <Card className="bg-white border-gray-border">
      <CardHeader className="pb-2">
        <CardTitle className="text-base font-heading">Recent Activity</CardTitle>
      </CardHeader>
      <CardContent className="max-h-[200px] overflow-y-auto">
        {activities.length > 0 ? (
          activities.slice(0, 8).map((a, i) => (
            <ActivityItem key={i} action={a.action || 'Activity recorded'} time={a.created_at ? new Date(a.created_at).toLocaleString() : 'Just now'} status="info" />
          ))
        ) : (
          <>
            <ActivityItem action="AI published 3 social posts" time="2 hours ago" status="success" />
            <ActivityItem action="2 new reviews received" time="5 hours ago" status="success" />
            <ActivityItem action="Email campaign sent to 1,240 contacts" time="Yesterday" status="success" />
          </>
        )}
      </CardContent>
    </Card>
  );
}


// ═══════════════════════════════════════════════════════════════
// MAIN DASHBOARD PAGE
// ═══════════════════════════════════════════════════════════════
const ROLE_LABELS = {
  super_admin: 'Platform Owner',
  country_operator: 'Country Operator',
  client_admin: 'Business Dashboard',
  staff: 'My Dashboard',
};

const ROLE_SUBTITLES = {
  super_admin: 'Global platform overview — operators, subscribers, and system health',
  country_operator: 'Your territory — top clients, feature adoption, and growth',
  client_admin: 'Your business at a glance — leads, marketing, service, and performance',
  staff: 'Your personal contribution — leads, tickets, KPIs, and goals',
};

export default function DashboardPage() {
  const navigate = useNavigate();
  const { effectiveRole } = useAuth();
  const { format: fc } = useCurrency();
  const { startTour } = useTour();
  const [stats, setStats] = useState(null);
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);

  // v2.5.888 — Auto-start the platform tour if we land here with ?tour=1
  // (used by Help & Tours → Replay Tour to guarantee the walkthrough fires).
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('tour') === '1') {
      // Wait for dashboard scorecards to mount before spotlighting them.
      const t = setTimeout(() => {
        startTour({ reset: false });
        // Clean the URL so refreshing doesn't re-fire the tour.
        window.history.replaceState({}, '', '/dashboard');
      }, 800);
      return () => clearTimeout(t);
    }
  }, [startTour]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [statsRes, activityRes] = await Promise.allSettled([
          axios.get(`${API_URL}/api/dashboard/stats`),
          axios.get(`${API_URL}/api/dashboard/activity`),
        ]);
        if (statsRes.status === 'fulfilled') setStats(statsRes.value.data);
        if (activityRes.status === 'fulfilled') setActivities(activityRes.value.data.activities || []);
      } catch (error) {
        console.error('Dashboard fetch error:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="h-8 w-8 text-emerald-brand animate-spin" />
      </div>
    );
  }

  const role = stats?.role || effectiveRole || 'staff';
  const title = ROLE_LABELS[role] || 'Dashboard';
  const subtitle = ROLE_SUBTITLES[role] || 'Your workspace';

  return (
    <div className="space-y-5 animate-fadeIn" data-testid="dashboard-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-xl sm:text-2xl lg:text-3xl font-heading font-bold text-text-primary">
            {title}<PageInfoTooltip pageKey="dashboard" />
          </h1>
          <p className="text-sm text-gray-medium">{subtitle}</p>
        </div>
        {(role === 'client_admin' || role === 'super_admin') && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button className="bg-emerald-brand hover:bg-emerald-hover gap-2" data-testid="quick-action-btn">
                <Plus className="h-4 w-4" /> Quick Action
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-52">
              <DropdownMenuItem onClick={() => navigate('/leads/pipeline')} className="gap-2 cursor-pointer">
                <Target className="h-4 w-4 text-blue-500" /> New Lead
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => navigate('/meeting')} className="gap-2 cursor-pointer">
                <Video className="h-4 w-4 text-emerald-500" /> New Meeting
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => navigate('/content')} className="gap-2 cursor-pointer">
                <Sparkles className="h-4 w-4 text-purple-500" /> Create Content
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={() => navigate('/service/helpdesk')} className="gap-2 cursor-pointer">
                <Headphones className="h-4 w-4 text-amber-500" /> New Ticket
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => navigate('/marketing/campaign-calendar')} className="gap-2 cursor-pointer">
                <Megaphone className="h-4 w-4 text-rose-500" /> New Campaign
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => navigate('/email')} className="gap-2 cursor-pointer">
                <Mail className="h-4 w-4 text-cyan-500" /> Send Email
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      </div>

      {role === 'super_admin' && <SuperAdminDashboard stats={stats} activities={activities} navigate={navigate} />}
      {role === 'country_operator' && <CountryOperatorDashboard stats={stats} activities={activities} navigate={navigate} />}
      {role === 'client_admin' && <NewSubscriberDashboard />}
      {role === 'staff' && <StaffDashboard stats={stats} navigate={navigate} />}
    </div>
  );
}
